import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../lib/firebase";
import { methodologyService } from "../../lib/firebase";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Zap, CheckCircle, XCircle, ExternalLink, AlertCircle } from "lucide-react";
import { toast } from "../../components/ui/use-toast";

const ADMIN_EMAIL = "admin@rawpa.com";

interface ReverseShellSettings {
  enabled: boolean;
  description: string;
  apiStatus: 'online' | 'offline' | 'unknown';
  lastChecked: string;
}

export const AdminReverseShellPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<ReverseShellSettings>({
    enabled: false,
    description: "Generate reverse shell payloads for various platforms and languages",
    apiStatus: 'unknown',
    lastChecked: new Date().toISOString()
  });
  const [saving, setSaving] = useState(false);

  // Check authentication
  useEffect(() => {
    if (!auth) {
      setError("Authentication service unavailable");
      setLoading(false);
      return () => {};
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/aAyuiOcvMq');
        return;
      }
      loadSettings();
      checkAPIStatus();
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const loadSettings = async () => {
    try {
      const systemSettings = await methodologyService.getSystemSettings();
      setSettings({
        enabled: !systemSettings.disableReverseShell,
        description: systemSettings.reverseShellDescription || "Generate reverse shell payloads for various platforms and languages",
        apiStatus: 'unknown',
        lastChecked: new Date().toISOString()
      });
    } catch (error) {
      console.error("Error loading Reverse Shell settings:", error);
      setError("Failed to load Reverse Shell settings");
    }
  };

  const checkAPIStatus = async () => {
    try {
      // Check if revshells.com is accessible
      const response = await fetch('https://www.revshells.com/', { 
        method: 'HEAD',
        mode: 'no-cors'
      });
      setSettings(prev => ({
        ...prev,
        apiStatus: 'online',
        lastChecked: new Date().toISOString()
      }));
    } catch (error) {
      console.error("Error checking Reverse Shell API status:", error);
      setSettings(prev => ({
        ...prev,
        apiStatus: 'offline',
        lastChecked: new Date().toISOString()
      }));
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await methodologyService.updateSystemSettings({
        disableReverseShell: !settings.enabled,
        reverseShellDescription: settings.description
      });
      
      toast({
        title: "Settings Saved",
        description: "Reverse Shell Generator settings have been updated successfully.",
      });
    } catch (error) {
      console.error("Error saving Reverse Shell settings:", error);
      toast({
        title: "Save Failed",
        description: "Failed to save Reverse Shell settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    try {
      if (auth) {
        await auth.signOut();
        navigate('/signin');
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getStatusIcon = () => {
    switch (settings.apiStatus) {
      case 'online':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'offline':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusText = () => {
    switch (settings.apiStatus) {
      case 'online':
        return 'Online';
      case 'offline':
        return 'Offline';
      default:
        return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/aAyuiOcvMq/admin')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/aAyuiOcvMq/toolkit')}
                className="font-['Space_Grotesk'] text-black"
              >
                Back to Toolkits
              </Button>
              <div 
                onClick={handleSignOut}
                className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
              >
                <img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
                  Reverse Shell Generator Management
                </h1>
                <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
                  Manage reverse shell payload generator integration
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Settings Panel */}
            <Card className="border-2 border-[#E7E7E7]">
              <CardHeader>
                <CardTitle className="text-xl text-black">Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base font-medium">Enable Reverse Shell Generator</Label>
                    <p className="text-sm text-gray-500">
                      Allow users to generate reverse shell payloads
                    </p>
                  </div>
                  <Switch
                    checked={settings.enabled}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label className="text-base font-medium">Description</Label>
                  <Textarea
                    value={settings.description}
                    onChange={(e) => setSettings(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter description for Reverse Shell Generator"
                    className="min-h-[100px]"
                  />
                </div>

                {/* Save Button */}
                <Button 
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="w-full bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                >
                  {saving ? "Saving..." : "Save Settings"}
                </Button>
              </CardContent>
            </Card>

            {/* Status Panel */}
            <Card className="border-2 border-[#E7E7E7]">
              <CardHeader>
                <CardTitle className="text-xl text-black">Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* API Status */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {getStatusIcon()}
                    <div>
                      <p className="font-medium text-black">API Status</p>
                      <p className="text-sm text-gray-500">{getStatusText()}</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={checkAPIStatus}
                  >
                    Refresh
                  </Button>
                </div>

                {/* Last Checked */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium text-black mb-1">Last Checked</p>
                  <p className="text-sm text-gray-500">
                    {new Date(settings.lastChecked).toLocaleString()}
                  </p>
                </div>

                {/* External Service Info */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <ExternalLink className="w-4 h-4 text-blue-600" />
                    <p className="font-medium text-blue-900">External Service</p>
                  </div>
                  <p className="text-sm text-blue-700">
                    This tool integrates with external reverse shell generation services 
                    to provide comprehensive payload generation capabilities.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* User Preview */}
          <Card className="mt-8 border-2 border-[#E7E7E7]">
            <CardHeader>
              <CardTitle className="text-xl text-black">User Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black">
                      Reverse Shell Generator
                    </h3>
                    <p className="text-sm text-gray-600">
                      {settings.description}
                    </p>
                  </div>
                  <Badge variant={settings.enabled ? "default" : "secondary"}>
                    {settings.enabled ? "Available" : "Disabled"}
                  </Badge>
                </div>
                
                {settings.enabled && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      Users will see Reverse Shell Generator as an option in their toolkit page with features:
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <Badge variant="outline">IP & Port</Badge>
                      <Badge variant="outline">Listener</Badge>
                      <Badge variant="outline">Shell Types</Badge>
                      <Badge variant="outline">Encoding</Badge>
                      <Badge variant="outline">Reverse</Badge>
                      <Badge variant="outline">Bind</Badge>
                      <Badge variant="outline">MSFVenom</Badge>
                      <Badge variant="outline">HoaxShell</Badge>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}; 